
import * as React from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mime from 'mime-types';
import {
  ColDef,
  GridApi,
  GridOptions,
  RowClickedEvent,
  ValueFormatterParams
} from 'ag-grid-community';
import {
  Box,
  Flex,
  Heading,
  useDisclosure,
  Button,
  Table,
  TableContainer,
  Tbody,
  Text,
  Link,
  Grid,
  GridItem,
  Tooltip,
  Spinner,
  Image,
  Select,
  Switch,
  Textarea,
  Td,
  Tr,
  FormHelperText,
  FormControl,
  Input
} from '@chakra-ui/react';
import SOTTaxStampsDataSource from '../ducks/SOTTaxStampsDatasource';
import { convertISTtoUTCDate, dateFormatterInMonth, getFormattedDate, getFormattedDateTime, getFormattedDateTimeFroManualReviewedAt, getFormattedDateTimeFromReviewedAt } from '@/queue/elements/value-formatters/date-formatter.mjs';
import { LicenseConfirmation, SOTTaxStamps, ReviewConfirmation } from '@/model/license-review.mjs';
import TableElement from '@/table/TableElement';
import HyperLink from '@/hyperlink/hyperlink';
import s3Utils from '../../utils/s3Utils';
import FFLDetailModal from '@/explore/ffl-detail/FFLDetailModal';
import { getIsGlobalAdmin } from '@/redux/app-selectors';
import AppConfig from '@/utils/app-config.mjs';
import { SERVER_PAGE_LICENSE_APPROVAL_LIST_SIZE } from '@/model/Constants';
import { agGridIconsConfig } from '@/explore/ffl-detail/utils.mjs';
import ControlledInput from '@/common/ControllerInput';
import {
  licenseDetailsNewApi,
  toggleSOTMarkedAsReviewedStatus,
  updateSOTTaxStampsStatus
} from '@/api/apis';
import ModalComponent from '@/common/Modal';
import { regexEmployeeIdentificationNumber, regexControlNumber, sotTaxStampsValidation, textareaLengthValidation } from '@/queue/ducks/validationSchema';
import PreviewLicense from '@/common/PreviewLicense';
import ConfirmationModal from '@/common/ConfirmationModal';
import { normalizeRejectedReasonFromApi, reviewFilters, SOURCE_OF_LICENSE, sotFailedSteps, reviewRejectedReasonsSot, stepMapping } from '@/utils/constants';
import { ShowPreview } from '@/common/PreviewLicense';
//@ts-ignore
import InputMask from 'react-input-mask';
import { getHideUnnecessarySOTRejectionValue, getSOTApprovalFilterModel, getSOTApprovalSort } from './selector';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import ReviewConfirmationModal from '@/common/ReviewConfirmationModal';
import { fixTimezoneOffset, trimDate } from '@/queue/utils';
import { getDocumentsRequiredDetails, getIsAssigningTransferDealer, getIsDealerDetailsLoading, getValidLicenseRecord } from '@/queue/ducks/selectors';
import ExploreV2 from '@/explore/Explore-v2';
import { setQuery } from '@/explore/ducks/slice';
import { useSearchParams } from 'react-router-dom';
import { setIsCertificateStatusUpdated, setSOTApprovalFilterModel, setSOTApprovalSearchText } from '../ducks/slice';
import { MapInteractionCSS } from "react-map-interaction";
import { getFormattedTextForManualReview } from '@/utils/utils';
import { callToGetDocumentStatus, callToGetLicenseRecordDetails, setDocumentsRequiredDetails } from '@/queue/ducks/slice';
import FlatDatePickr from '@/common/Flatpickr';
import {
  SOTDocumentsGridView,
  type DocumentCardHoverReviewKind,
  type DocumentGridRotateKind
} from './documents-grid-view';
import {
  isRasterImageForBrowserRotation,
  normalizeStorageKeyFromApi,
  rotateRasterImageBlob,
  uploadRotatedRasterToLicenseReader
} from '@/utils/rotateImageAndUploadS3';

const SOT_GALLERY_THUMB_S3_BUST_KEY =
  'ffl_bo_sot_gallery_thumb_s3_overwrite_v1';

function readSotGalleryThumbS3BustMap(): Record<string, number> {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(SOT_GALLERY_THUMB_S3_BUST_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [docId, v] of Object.entries(parsed as Record<string, unknown>)) {
      const n = typeof v === 'number' ? v : Number(v);
      if (docId && !Number.isNaN(n) && n >= 1) out[docId] = n;
    }
    return out;
  } catch {
    return {};
  }
}

function persistSotGalleryThumbS3Bust(docId: string, nonce: number) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const map = readSotGalleryThumbS3BustMap();
    map[docId] = Math.max(map[docId] ?? 0, nonce);
    sessionStorage.setItem(SOT_GALLERY_THUMB_S3_BUST_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

type SotRasterBucket = 'sotTaxStamps' | 'licenseReader';

function SOTTableViewIcon({ color }: { color: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={14}
      height={14}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
    >
      <path
        d="M5.25 1.75H2.91667C2.60725 1.75 2.3105 1.87292 2.09171 2.09171C1.87292 2.3105 1.75 2.60725 1.75 2.91667V5.25M5.25 1.75H11.0833C11.3928 1.75 11.6895 1.87292 11.9083 2.09171C12.1271 2.3105 12.25 2.60725 12.25 2.91667V5.25M5.25 1.75V12.25M1.75 5.25V11.0833C1.75 11.3928 1.87292 11.6895 2.09171 11.9083C2.3105 12.1271 2.60725 12.25 2.91667 12.25H5.25M1.75 5.25H12.25M12.25 5.25V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H5.25"
        stroke={color}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SOTGalleryViewIcon({ color }: { color: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={14}
      height={14}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
    >
      <path
        d="M7 1.75V12.25"
        stroke={color}
        strokeWidth="1.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.75 7H12.25"
        stroke={color}
        strokeWidth="1.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.0833 1.75H2.91667C2.27233 1.75 1.75 2.27233 1.75 2.91667V11.0833C1.75 11.7277 2.27233 12.25 2.91667 12.25H11.0833C11.7277 12.25 12.25 11.7277 12.25 11.0833V2.91667C12.25 2.27233 11.7277 1.75 11.0833 1.75Z"
        stroke={color}
        strokeWidth="1.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface props {
  doRefresh: number;
  toolbarRefreshNonce: number;
  tabName: string | undefined;
  className: string;
  gridRef: React.RefObject<AgGridReact<SOTTaxStamps>>;
}

function SOTTaxStampsTable({
  doRefresh,
  toolbarRefreshNonce,
  tabName,
  className,
  gridRef,
}: props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isLicenseModalOpen,
    onOpen: onLicenseModalOpen,
    onClose: onLicenseModalClose
  } = useDisclosure();
  const {
    isOpen: isHyperLinkOpen,
    onOpen: onHyperLinkOpen,
    onClose: onHyperLinkClose
  } = useDisclosure();
  const [licenseReview, setLicenseReview] = useState<SOTTaxStamps | null>(
    null
  );
  const [reviewInputVal, setReviewInputVal] = useState<any>({
    rejectReason: "",
    note: "",
  });
  const [initialLicenseData, setInitialLicenseData] =
    useState<SOTTaxStamps | null>(null);
  const {
    isOpen: isLicensePreviewOpen,
    onOpen: onLicensePreviewOpen,
    onClose: onLicensePreviewClose
  } = useDisclosure();
  const {
    isOpen: isReviewConfirmationMadalOpen,
    onClose: onCloseReviewConfirmationModal,
    onOpen: onOpenReviewConfirmationModal
  } = useDisclosure();
  const {
    isOpen: isSearchNewDealerModalOpen,
    onClose: onCloseSearchNewDealerModal,
    onOpen: onOpenSearchNewDealerModal
  } = useDisclosure();
  const hideRejectionSOT = getHideUnnecessarySOTRejectionValue()
  useEffect(() => {
    if (isSearchNewDealerModalOpen) onClose()

  }, [isSearchNewDealerModalOpen])
  const dispatch = useDispatch();
  const [licenseeId, setLicenseeId] = useState<string>('');
  const [ocrExpDate, setOcrExpDate] = useState<any>(null);
  const [signatureDate, setSignatureDate] = useState<any>(null);
  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);
  const dataSource = useMemo(() => new SOTTaxStampsDataSource(), []);
  const [isApprovingLicenseStatus, setIsApprovingLicenseStatus] =
    useState<boolean>(false);
  const [isRejectingLicenseStatus, setIsRejectingLicenseStatus] =
    useState<boolean>(false);
  const blob = new Blob();
  const [licenseBlob, setLicenseBlob] = useState<Blob | null>(blob);
  const [reviewConfirmation, setReviewConfirmation] = useState<ReviewConfirmation>({
      header: "",
      isCancelBtnDisable: false,
      isConfirmBtnDisable: false,
      isConfirmBtnLoading: false,
      isSaveBtnLoading: false,
      rejectedReason: "",
      note: "",
      error: "",
      status: "",
  });
  const sotApprovalFilterModel = getSOTApprovalFilterModel();
  const isAssigningTransferDealer = getIsAssigningTransferDealer();
  const [licenseKey, setLicenseKey] = useState<string>('');
  const [notFoundError, setNotFoundError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [license, setLicense] = useState<any>();
  const [isEditingEnabled, setIsEditingEnabled] = useState<boolean>(false);
  const [textareaEvent, setTextareaEvent] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const [downloadingFile2, setDownloadingFile2] = useState<boolean>(false);
  const sotApprovalSort = getSOTApprovalSort();
  const [sotListView, setSotListView] = useState<'table' | 'gallery'>('table');
  const [documentsGalleryRefreshKey, setDocumentsGalleryRefreshKey] =
    useState(0);
  const [galleryImagePathOverrideByDocId, setGalleryImagePathOverrideByDocId] =
    useState<Record<string, string>>({});
  const [galleryThumbnailReloadNonceByDocId, setGalleryThumbnailReloadNonceByDocId] =
    useState<Record<string, number>>(readSotGalleryThumbS3BustMap);
  const isSotReviewModalOpenRef = useRef(false);
  const sotReviewRowIdRef = useRef<string | null>(null);
  const pendingRotatedBlobByDocIdRef = useRef<Map<string, Blob>>(new Map());
  const rotateGenerationByDocIdRef = useRef<Record<string, number>>({});
  const sotRotateTargetRef = useRef<{
    docId: string;
    key: string;
    bucket: SotRasterBucket;
  } | null>(null);
  const galleryOverrideSnapshotRef = useRef(galleryImagePathOverrideByDocId);
  galleryOverrideSnapshotRef.current = galleryImagePathOverrideByDocId;

  useEffect(() => {
    isSotReviewModalOpenRef.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    sotReviewRowIdRef.current = licenseReview?.id ?? null;
  }, [licenseReview?.id]);
  useEffect(
    () => () => {
      Object.values(galleryOverrideSnapshotRef.current).forEach((v) => {
        if (v && /^blob:/i.test(v)) {
          URL.revokeObjectURL(v);
        }
      });
    },
    []
  );

  interface Props {
    valueFormatted: string;
    value: string;
  }

  const principalBusinessAddress = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;
  const physicalBusinessAddress = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;

  useEffect(() => {
    principalBusinessAddress?.current?.setSelectionRange(textareaEvent, textareaEvent);
    physicalBusinessAddress?.current?.setSelectionRange(textareaEvent, textareaEvent);
  }, [licenseReview?.name_and_principal_business_address, licenseReview?.actual_physical_business_address])

  const handleSortByChange = React.useCallback(() => {
    if (dataSource && gridRef.current?.api) {
      // Show loading overlay immediately
      gridRef.current.api.showLoadingOverlay();
      
      // Update datasource with new sortBy value
      dataSource.setSortBy(sotApprovalSort === 'true');
      
      // Refresh server-side data
      setTimeout(() => {
        gridRef.current?.api.refreshServerSide();
      }, 50); // Small delay to ensure loading overlay is visible
    }
  }, [sotApprovalSort, dataSource]);

  useEffect(() => {
    handleSortByChange();
  }, [handleSortByChange]);

  useEffect(() => {
    if (doRefresh === 2) reFetchData();
  }, [doRefresh]);

  useEffect(() => {
    if (toolbarRefreshNonce > 0) {
      setDocumentsGalleryRefreshKey((k) => k + 1);
    }
  }, [toolbarRefreshNonce]);
  function renderHyperLink(data: any, columnName: string, fullLicenseKey: string | null = null) {
    let renderValue = '';
    let linkUrl = '';
    if (columnName === "full_license_key") {
      linkUrl = `/dealers-search/${data}`;
      renderValue = (data && fullLicenseKey) ? fullLicenseKey : "NONE";
    } else if (columnName === "transfer_id") {
      linkUrl = `/transfers/${data}`;
      renderValue = data || '';
    }
    const ref = (ref: any) => {
      if (!ref) return;
      ref.onclick = (e: any) => {
        e.stopPropagation();
        window.open(linkUrl, '_blank');
      };
    };
    const text = (color: string) => (
      <Text color={color}>{renderValue}</Text>
    );
    return (!renderValue || renderValue === 'NONE') ? (
      text('black')
    ) : (
      <Link ref={ref} href="#">
        {text('#0065FF')}
      </Link>
    );
  }

  const status: { [key: string]: string } = {
    "pending": "orange",
    "rejected": "red",
    "approved": "green",
    "reviewed": "blue",
  };

  interface OperationTypes {
    [key: string]: {
      name: string,
      rate: number
    }
  }

  const operationTypes: OperationTypes = {
    "61": {
      name: "Class 1 - Importer of Firearms",
      rate: 1000
    },
    "71": {
      name: "Class 1 - Importer of Firearms (Reduced)",
      rate: 500
    },
    "62": {
      name: "Class 2 - Manufacturer of Firearms",
      rate: 1000
    },
    "72": {
      name: "Class 2 - Manufacturer of Firearms (Reduced)",
      rate: 500
    },
    "63": {
      name: "Class 3 - Dealer in Firearms",
      rate: 500
    }
  };

  function StatusCellRenderer(value: any) {
    return (
      <Box>
        <Box as={'span'} color={status[value?.data?.status] || ""} marginRight={'0.25rem'}>
          {'\u2B24'}
        </Box>
        {value?.data?.status ? (value.data.status[0].toUpperCase() + value.data.status.slice(1)) : ""}
      </Box>
    );
  }
  function ActionPerformedByCellRenderer({ data, value }:any) { 
    const actionPerformedBy: any = {
      System: 'backend',
      Agent: 'profile',
      Dealer: 'dealer',
      system: 'backend',
      agent: 'profile',
      dealer: 'dealer',
      Support: 'profile',
      support: 'profile',
      SMS: 'dealer',
      sms: 'dealer',
      email_campaign: 'email',
      ffl360: 'profile'
    }
    const email = data?.manual_review_requested_by && data?.manual_review_requested_by !==null ? data?.manual_review_requested_by : '';
    if (value && value !== null) {
      if (value?.toLowerCase() === "email_campaign") {
        return `Via Dealer ${email}`;
      }
      if (value?.toLowerCase() === "dealer" || value?.toLowerCase() === "upload_ui") {
        return `Via Dealer ${email}` ;
      }
      if (actionPerformedBy[value]) {
        return `Via ${actionPerformedBy[value]} ${email}`;
      }
      return ''
    }
    return ''
  }
  function renderBusinessName({data}: ValueFormatterParams) {
    if(licenseReview?.business_name || data?.business_name){
      return <Box> {licenseReview?.business_name || data?.business_name} </Box>
    }
    return 'NONE';
  }

  function reFetchData() {
    gridRef.current?.api?.refreshServerSide();
  }

  const getFailedStep = (value: string | number | null) => {
    if (value == null || value === '') return '';
    return stepMapping[String(value)] || '';
  };


  function SourceCellRenderer({ data }: ValueFormatterParams) {
    return (
      <Box>
        {SOURCE_OF_LICENSE[data?.source] || ''}
      </Box>
    );
  }

  const DateCellRenderer = ({ value, data }: ValueFormatterParams) => {
    return convertISTtoUTCDate(value) ? `${convertISTtoUTCDate(value)} - ${convertISTtoUTCDate(data?.special_tax_period_end_date)}` : '';
  }

  const columnDefs: ColDef[] = [
    {
      field: 'transfer_id',
      headerName: 'Transfer ID',
      cellRenderer: ({ value, valueFormatted }: Props) => renderHyperLink(value, "transfer_id"),
      width: 240,
      suppressSizeToFit: true
    },
    {
      field: 'full_license_key',
      headerName: 'License Number',
      valueFormatter: ({ data }: ValueFormatterParams<SOTTaxStamps>) => {
        if (data?.full_license_key && data?.full_license_key.length === 20) {
          return data?.full_license_key;
        }
        return 'NONE';
      },
      cellRenderer: (data: any) => renderHyperLink(data?.data?.dealer_id, "full_license_key", data?.valueFormatted),
      width: 200,
      suppressSizeToFit: true
    },
    {
      field: 'business_name',
      headerName: 'Principal License Name',
      cellRenderer: renderBusinessName,
      width: 350,
      suppressSizeToFit: true
    },
    {
      field: 'status',
      headerName: 'Status',
      cellRenderer: StatusCellRenderer,
      width: 200,
      filter: "agSetColumnFilter",
      filterParams: {
        values: reviewFilters.status.map((item: any) => item.value),
      },
      suppressSizeToFit: true
    },
    {
      field: 'special_tax_period_start_date',
      headerName: 'Dates of Special Tax Period',
      cellRenderer:DateCellRenderer,
      width: 250,
      suppressSizeToFit: true
    },
    {
      field: 'reviewed_at',
      headerName: 'Review Date',
      valueFormatter: getFormattedDateTimeFromReviewedAt,
      width: 150,
      suppressSizeToFit: true
    },
    {
      field: 'reviewed_by',
      headerName: 'Reviewer',
      cellRenderer: ({ value }: Props) => value || '',
      width: 150,
      suppressSizeToFit: true
    },
    
    {
      field: 'source',
      headerName: 'Source of SOT',
      cellRenderer: SourceCellRenderer,
      width: 240,
      filter: "agSetColumnFilter",
      filterParams: {
        values: reviewFilters.source.map((item: any) => item.value),
      },
      suppressSizeToFit: true
    },
    {
      field: 'created_by',
      headerName: 'Uploaded By',
      cellRenderer: ({ data }: ValueFormatterParams) => {
        return data?.source === 'ffl360' ? (data?.created_by || '-') : '-';
      },
      width: 200,
      suppressSizeToFit: true
    },
    {
      field: 'tax_stamp_upload_date',
      headerName: 'Uploaded Date',
      valueFormatter: ({ data }: ValueFormatterParams) => {
        const date = data?.tax_stamp_upload_date ? data["tax_stamp_upload_date"] : null
        return dateFormatterInMonth(date);
      },
      width: 250,
      filter: 'agDateColumnFilter',
      filterParams: {
        includeBlanksInRange: true
      },
      suppressSizeToFit: true
    },
    {
      field: 'updated_at',
      headerName: 'Updated Date',
      valueFormatter: ({ value }: { value: string }) => {
        return dateFormatterInMonth(value) || '';
      },
      width: 250,
      suppressSizeToFit: true
    },
    {
      field: 'error_code',
      headerName: 'Failure Reason',
      cellRenderer: ({ value }: { value: string }) => {
        if (value) return getFailedStep(value) || '';
        else return '';
      },
      width: 350,
      filter: "agSetColumnFilter",
      filterParams: {
        values: [...reviewFilters.sotFailedSteps.map((item: any) => item.value), "other"],
      },
      suppressSizeToFit: true
    },
    {
      field: 'rejected_reason',
      headerName: 'Rejection Reason',
      cellRenderer: ({ value }: { value: string | null | undefined }) => {
        return normalizeRejectedReasonFromApi(value ?? null) || '';
      },
      width: 350,
      suppressSizeToFit: true
    },
    {
      field: 'note',
      headerName: 'Rejection Note',
      cellRenderer: ({ value }: { value: string }) => {
        return value || '';
      },
      width: 350,
      tooltipField: 'note',
      suppressSizeToFit: true
    },
    {
      field: 'reference_id',
      headerName: 'Order ID',
      cellRenderer: ({ value, valueFormatted }: Props) => {
        return value || '';
      },
      width: 240,
      filter: 'agTextColumnFilter',
      suppressSizeToFit: true
    },
    {
      field: 'store_name',
      headerName: 'Store Name',
      cellRenderer: ({ value }: Props) => value || '',
      width: 240,
      suppressSizeToFit: true
    },
    {
      field: 'action_performed_by',
      headerName: 'Manual  Review Requester',
      cellRenderer: ActionPerformedByCellRenderer,
      width: 300,
      suppressSizeToFit: true
    },
    {
      field: 'manual_review_requested_at',
      headerName: 'Manual Review Request Date',
      cellRenderer: getFormattedDateTimeFroManualReviewedAt,
      width: 300,
      suppressSizeToFit: true
    },
    {
      field: 'searchText',
      hide: true,
      filter: 'agTextColumnFilter',
      suppressSizeToFit: true
    },
    {
      field: 'is_external',
      headerName: 'Store Type',
      cellRenderer: ({ value, valueFormatted }: Props) => {
        if (['true'].includes(`${value}`)) return 'Live';
        else if (['false'].includes(`${value}`)) return 'Testing';
        else return '';
      },
      width: 200,
      filter: "agSetColumnFilter",
      filterParams: {
        values: [...reviewFilters.storeType.map((item: any) => item.value), 'false'],
        valueFormatter: (params: { value: string }) => {
          if (params.value === 'true') return 'Live';
          if (params.value === 'false') return 'Testing';
          return '';
        },
      },
      suppressSizeToFit: true
    },
    {
      field: 'license_path',
      headerName: 'File Path',
      cellRenderer: ({ value }: Props) => value || '',
      width: 350,
      hide: true,
      suppressSizeToFit: true
    },
  ];
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  );

  const fullURL = (path: string) => `${AppConfig.SOTTaxStampsBaseURL}${encodeURIComponent(path)}`;

  const populateSOTRowReviewState = (eventData: SOTTaxStamps) => {
    const expDate = eventData?.ocr_expiry_date;
    if (expDate) {
      setOcrExpDate(getFormattedDate(expDate));
    } else {
      setOcrExpDate(null);
    }
    const prtDate = eventData?.printed_date;
    if (prtDate) {
      setSignatureDate(getFormattedDate(prtDate));
    } else {
      setSignatureDate(null);
    }
    setLicenseReview({
      ...eventData,
      date_of_tax_stamp_receipt: eventData?.date_of_tax_stamp_receipt
        ? getFormattedDate(trimDate(eventData?.date_of_tax_stamp_receipt))
        : '',
      special_tax_period_end_date: eventData?.special_tax_period_end_date
        ? getFormattedDate(trimDate(eventData?.special_tax_period_end_date))
        : '',
      special_tax_period_start_date: eventData?.special_tax_period_start_date
        ? getFormattedDate(trimDate(eventData?.special_tax_period_start_date))
        : '',
      control_number: eventData?.control_number
        ? formatControlNumber(eventData.control_number)
        : '',
      employee_identification_number: eventData?.control_number
        ? formatEmployeeIdentificationNumber(eventData.employee_identification_number)
        : '',
      bio_metric_scanner: eventData?.bio_metric_scanner || false,
      name_and_principal_business_address:
        eventData?.name_and_principal_business_address || '',
      actual_physical_business_address:
        eventData?.actual_name_and_physical_business_address ||
        eventData?.actual_physical_business_address ||
        ''
    });
    setReviewInputVal({
      rejectReason:
        normalizeRejectedReasonFromApi(eventData?.rejected_reason ?? null) || '',
      note: eventData?.note || ''
    });
    setInitialLicenseData(eventData);
    setLicenseKey(eventData.license_key || '');
  };

  const openSOTDetailPage = (data: SOTTaxStamps) => {
    window.open(`/admin/sot-tax-stamps/${data?.id as string}`, '_blank');
  };

  const openRejectFromGalleryRow = (data: SOTTaxStamps) => {
    populateSOTRowReviewState(data);
    setReviewConfirmation((prevState: any) => ({
      ...prevState,
      header: 'Reason of Rejection',
      status: 'rejected'
    }));
    onOpenReviewConfirmationModal();
  };

  const markSOTReviewedFromGallery = async (doc: SOTTaxStamps) => {
    if (!doc.id) return;
    try {
      const response = await toggleSOTMarkedAsReviewedStatus(doc.id);
      if (response.status === 200) {
        dispatch(setIsCertificateStatusUpdated(true));
        reFetchData();
        setDocumentsGalleryRefreshKey((k) => k + 1);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getSOTGalleryHoverReviewKind = (
    d: SOTTaxStamps
  ): DocumentCardHoverReviewKind | undefined => {
    if (!d.id) return undefined;
    return d.reviewed_in_grid_by ? 'done' : 'action';
  };

  async function resolveSotRasterLocationForRotate(
    doc: SOTTaxStamps
  ): Promise<{ key: string; bucket: SotRasterBucket } | null> {
    const lp = normalizeStorageKeyFromApi(doc.license_path || '');
    if (lp) {
      const b = await s3Utils.getFileBlob(lp, 'sotTaxStamps', { public: '' }, {
        bypassHttpCache: true
      });
      if (b?.size) return { key: lp, bucket: 'sotTaxStamps' };
    }
    const op = normalizeStorageKeyFromApi(doc.object_path || '');
    if (op) {
      const b = await s3Utils.getFileBlob(op, 'licenseReader', { public: '' }, {
        bypassHttpCache: true
      });
      if (b?.size) return { key: op, bucket: 'licenseReader' };
    }
    return null;
  }

  const performRotateForSOTDoc = async (
    doc: SOTTaxStamps,
    kind: DocumentGridRotateKind
  ) => {
    const resolved = await resolveSotRasterLocationForRotate(doc);
    if (!resolved) {
      console.error(
        'SOT rotate: could not resolve image in sotTaxStamps or licenseReader.',
        doc.id
      );
      return;
    }
    const { key: sourceKey, bucket } = resolved;
    if (sourceKey.toLowerCase().endsWith('.pdf')) {
      return;
    }
    sotRotateTargetRef.current = { docId: doc.id, key: sourceKey, bucket };
    rotateGenerationByDocIdRef.current[doc.id] =
      (rotateGenerationByDocIdRef.current[doc.id] || 0) + 1;
    const genAtRotate = rotateGenerationByDocIdRef.current[doc.id];

    try {
      let blob = await s3Utils.getFileBlob(sourceKey, bucket, { public: '' }, {
        bypassHttpCache: true
      });
      if (!blob?.size) {
        console.error('SOT rotate: empty download from S3.', sourceKey, bucket);
        return;
      }
      const ext = sourceKey.split('.').pop() as string;
      const contentType =
        mime.lookup(ext) || blob.type || 'application/octet-stream';
      blob = new Blob([blob], { type: contentType });
      if (!isRasterImageForBrowserRotation(sourceKey, blob.type)) {
        return;
      }

      const { blob: rotatedBlob, contentType: outContentType } =
        await rotateRasterImageBlob(blob, kind);

      const blobUrl = URL.createObjectURL(rotatedBlob);
      setGalleryImagePathOverrideByDocId((prev) => {
        const prevUrl = prev[doc.id];
        if (prevUrl && /^blob:/i.test(prevUrl)) {
          URL.revokeObjectURL(prevUrl);
        }
        return { ...prev, [doc.id]: blobUrl };
      });
      pendingRotatedBlobByDocIdRef.current.set(doc.id, rotatedBlob);

      if (
        isSotReviewModalOpenRef.current &&
        sotReviewRowIdRef.current === doc.id
      ) {
        setLicenseBlob(rotatedBlob);
      }

      void (async () => {
        try {
          await uploadRotatedRasterToLicenseReader({
            path: sourceKey,
            blob: rotatedBlob,
            contentType: outContentType,
            bucket
          });
          if (rotateGenerationByDocIdRef.current[doc.id] !== genAtRotate) {
            return;
          }
          setGalleryImagePathOverrideByDocId((prev) => {
            const cur = prev[doc.id];
            if (cur && /^blob:/i.test(cur)) {
              const u = cur;
              window.setTimeout(() => URL.revokeObjectURL(u), 5000);
            }
            const { [doc.id]: _removed, ...rest } = prev;
            return rest;
          });
          pendingRotatedBlobByDocIdRef.current.delete(doc.id);
          setGalleryThumbnailReloadNonceByDocId((prev) => {
            const next = (prev[doc.id] ?? 0) + 1;
            persistSotGalleryThumbS3Bust(doc.id, next);
            return { ...prev, [doc.id]: next };
          });
          if (
            isSotReviewModalOpenRef.current &&
            sotReviewRowIdRef.current === doc.id
          ) {
            let refreshed = await s3Utils.getFileBlob(
              sourceKey,
              bucket,
              { public: '' },
              { bypassHttpCache: true }
            );
            const ct =
              mime.lookup(sourceKey.split('.').pop() as string) ||
              refreshed.type ||
              'application/octet-stream';
            refreshed = new Blob([refreshed], { type: ct });
            setLicenseBlob(refreshed);
          }
        } catch (e) {
          console.error(e);
          if (rotateGenerationByDocIdRef.current[doc.id] !== genAtRotate) {
            return;
          }
          setGalleryImagePathOverrideByDocId((prev) => {
            const cur = prev[doc.id];
            if (cur && /^blob:/i.test(cur)) {
              const u = cur;
              window.setTimeout(() => URL.revokeObjectURL(u), 5000);
            }
            const { [doc.id]: _removed, ...rest } = prev;
            return rest;
          });
          pendingRotatedBlobByDocIdRef.current.delete(doc.id);
        }
      })();
    } catch (e) {
      console.error(e);
    }
  };

  function onCloseLicensePreview() {
    setDownloadingFile(false);
    const blob = new Blob();
    setLicenseBlob(blob);
    onLicensePreviewClose();
  }

  const gridStyle = useMemo(() => ({ width: '100%' }), []);

  const previousFilterModel = useRef<any>(null);

  const applySOTTableFilters = useCallback(
    (api: GridApi) => {
      api.setFilterModel({
        ...sotApprovalFilterModel,
        error_code: sotApprovalFilterModel?.faildSteps,
        is_external: sotApprovalFilterModel?.storeType,
        isManuallyRequested: sotApprovalFilterModel?.isManuallyRequested,
        tax_stamp_upload_date:
          sotApprovalFilterModel?.created_date_time?.dateFrom &&
          sotApprovalFilterModel?.created_date_time?.dateTo &&
          new Date(sotApprovalFilterModel.created_date_time.dateFrom) <=
            new Date(sotApprovalFilterModel.created_date_time.dateTo)
            ? sotApprovalFilterModel.created_date_time
            : {}
      });

      const newContext: any = {
        customFilters: {
          hideRejection: !hideRejectionSOT
        }
      };
      if (sotApprovalFilterModel?.isManuallyRequested) {
        previousFilterModel.current = sotApprovalFilterModel;
        newContext.customFilters.isManuallyRequested =
          sotApprovalFilterModel.isManuallyRequested.values || [];
      }

      api.setGridOption('context', newContext);
      api.refreshServerSide();
    },
    [sotApprovalFilterModel, hideRejectionSOT]
  );

  const gridOptions: GridOptions<SOTTaxStamps> = {
    rowModelType: 'serverSide',
    pagination: true,
    paginationPageSize: SERVER_PAGE_LICENSE_APPROVAL_LIST_SIZE,
    cacheBlockSize: SERVER_PAGE_LICENSE_APPROVAL_LIST_SIZE,
    serverSideDatasource: dataSource,
    // // generate a unique ID for each row based on the abuse ID
    getRowId: (params) => {
      return params.data.id;
    },
    getRowStyle: (params) => {
      if (params.data?.action_performed_by !== null &&
        params.data?.action_performed_by !== undefined &&
        params.data?.manual_review_requested_at !== null &&
        params.data?.manual_review_requested_at !== undefined &&
        params?.data?.status === 'pending'
      ) {
        return { 'background-color': 'rgba(255, 0, 0, 0.15)' };
      }
    },
    // column definitions
    columnDefs,
    defaultColDef,

    // row options
    animateRows: true,
    suppressRowClickSelection: true,
    onRowClicked: (event: RowClickedEvent<SOTTaxStamps>) => {
      if (gridRef.current && event.node) {
        gridRef.current.api.deselectAll();
        event.node.setSelected(true);
      }
    },
    onRowDoubleClicked: (event: RowClickedEvent<SOTTaxStamps>) => {
      if (event.data) {
        populateSOTRowReviewState(event.data);
        window.open(`/admin/sot-tax-stamps/${event.data?.id as string}`, '_blank');
      }
    },

    // cell detail options
    suppressCellFocus: true,
    // misc options
    debug: false,
    onGridReady(params) {
      const api = params.api;
      api.sizeColumnsToFit();
      applySOTTableFilters(api);

      const syncNoRowsOverlay = () => {
        const rowCount = api.getDisplayedRowCount();
        if (rowCount) api.hideOverlay();
        else api.showNoRowsOverlay();
      };
      syncNoRowsOverlay();
      api.addEventListener('modelUpdated', syncNoRowsOverlay);
    },
    icons: agGridIconsConfig,
    overlayNoRowsTemplate:
      '<div class="not-found-box"><img src="/images/ui/not-found-icon.png"></img><p class="not-found-box__title">No SOT Tax Stamps found</p><p class="not-found-box__message">Try adjusting your filter to find what you’re looking for</p></div>'
  };

  useEffect(()=>{
    reFetchData()
  },[hideRejectionSOT])

  useEffect(() => {
    if (sotListView !== 'table') return;
    const api = gridRef.current?.api;
    if (!api) return;
    applySOTTableFilters(api);
  }, [sotListView, applySOTTableFilters])

  useEffect(() => {
    if (searchParams.get('transferId') && tabName === 'sot-tax-stamps') {
      dispatch(setSOTApprovalSearchText(searchParams.get('transferId') || ''));
      dispatch(
        setSOTApprovalFilterModel({
          ...sotApprovalFilterModel,
          status: {
            ...sotApprovalFilterModel.status,
            values: []
          },
          searchText: {
            ...sotApprovalFilterModel.searchText,
            filter: searchParams.get('transferId') || ''
          },
        })
      );
    } else {
      dispatch(setSOTApprovalSearchText(''));
      dispatch(
        setSOTApprovalFilterModel({
          ...sotApprovalFilterModel,
          status: {
            ...sotApprovalFilterModel.status,
            values: ['pending']
          },
          searchText: {
            ...sotApprovalFilterModel.searchText,
            filter: ''
          },
        })
      );
    }
  }, [searchParams])

  const isSuperAdmin = getIsGlobalAdmin();

  function renderData() {
    return (
      <FFLDetailModal
        id={licenseeId}
        isOpen={isHyperLinkOpen ? isHyperLinkOpen : isLicenseModalOpen}
        onClose={isHyperLinkOpen ? onHyperLinkClose : onLicenseModalClose}
        cognitoGroup={false}
        isSuperAdmin={isSuperAdmin}
      />
    );
  }

  const startDateSpecialTaxPickerRef = useRef<any>(null);
  const endDateSpecialTaxPickerRef = useRef<any>(null);
  const dateReceiptPickerRef = useRef<any>(null);

  const openStartDateSpecialTaxDatePicker = () => {
    if (startDateSpecialTaxPickerRef.current) {
      startDateSpecialTaxPickerRef.current.setOpen(true);
    }
  };

  const openEndDateSpecialTaxDatePicker = () => {
    if (endDateSpecialTaxPickerRef.current) {
      endDateSpecialTaxPickerRef.current.setOpen(true);
    }
  };

  const openReceiptDatePicker = () => {
    if (dateReceiptPickerRef.current) {
      dateReceiptPickerRef.current.setOpen(true);
    }
  };

  const formatLiceseKey = (licenseKey: string) => {
    return licenseKey ? `${licenseKey.slice(0, 1)}-${licenseKey.slice(1, 3)}-xxx-xx-xx-${licenseKey.slice(3)}` : "";
  };


  const checkStatus = (currentStatus: string | undefined, status: string) => {
    return currentStatus === status;
  };

  const onDateChange = async (date: Date, fieldName: string) => {
    console.log({ fieldName: getFormattedDate(date.toDateString()) });

    setLicenseReview((prevState: any) => ({
      ...prevState,
      [fieldName]: getFormattedDate(date.toDateString()),
      errors: { ...(prevState.errors || {}), [fieldName]: '' }
    }));
  }

  const onOpenCertificateStatus = async (status: string) => {
    if (status === "approved") {
      await validateFields();
      setReviewConfirmation((prevState: any) => ({
        ...prevState,
        header: "Reason of Approval",
        status: status
      }))
    } else {
      setReviewConfirmation((prevState: any) => ({
        ...prevState,
        header: "Reason of Rejection",
        status: status
      }))
    }
    onOpenReviewConfirmationModal();
  }

  const updateCertificateStatus = async (status: string,isSaveClicked:boolean=false, isEmailIgnored: boolean=false) => {
    try {
      setReviewConfirmation((prevState: ReviewConfirmation) => ({
        ...prevState,
        isConfirmBtnLoading: !isSaveClicked ? true : isSaveClicked,
        isCancelBtnDisable: true,
        isSaveBtnLoading: isSaveClicked,
      }));
      const response = await updateSOTTaxStampsStatus(
        (status === "approved" || isSaveClicked) ? {
          dealer_id: licenseReview?.dealer_id,
          id: licenseReview?.id,
          license_key: licenseReview?.license_key,
          business_name: licenseReview?.business_name,
          special_tax_period_start_date: licenseReview?.special_tax_period_start_date,
          special_tax_period_end_date: licenseReview?.special_tax_period_end_date,
          date_of_tax_stamp_receipt: licenseReview?.date_of_tax_stamp_receipt,
          type_of_operation: licenseReview?.type_of_operation,
          name_and_principal_business_address: licenseReview?.name_and_principal_business_address,
          actual_physical_business_address: licenseReview?.actual_physical_business_address,
          bio_metric_scanner: licenseReview?.bio_metric_scanner,
          license_path: licenseReview?.license_path,
          annual_tax_rate: licenseReview?.annual_tax_rate,
          number_of_locations: licenseReview?.number_of_locations,
          principal_business_name: licenseReview?.principal_business_name,
          actual_physical_business_name: licenseReview?.actual_physical_business_name,
          status,
          employee_identification_number: licenseReview?.employee_identification_number.split("-").join(""),
          control_number: licenseReview?.control_number.split("-").join(""),
          rejected_reason: reviewConfirmation?.rejectedReason,
          note: reviewConfirmation?.note,
          source: licenseReview?.source,
          ignoreManualReviewEmail: isEmailIgnored
        } : {
          dealer_id: licenseReview?.dealer_id,
          id: licenseReview?.id,
          license_key: licenseReview?.license_key,
          status,
          business_name: licenseReview?.business_name,
          rejected_reason: reviewConfirmation?.rejectedReason,
          note: reviewConfirmation?.note,
          ignoreManualReviewEmail: isEmailIgnored
        }
      );
      if (response.status === 204) {
        dispatch(setIsCertificateStatusUpdated(true));
        reFetchData();
        setDocumentsGalleryRefreshKey((k) => k + 1);
      }
      closeReviewConfirmationModal();
      onClose();
    } catch (err: any) {
      console.log('ERROR', err);
      if (
        err &&
        err?.response &&
        err.response?.status == 404 &&
        err.response?.data &&
        err.response.data?.Error
      ) {
        setLicenseReview((prevState: any) => ({
          ...prevState,
          errors: { ...(prevState.errors || {}), 'license_key': err.response.data?.Error }
        }))
      }
      setReviewConfirmation((prevState: ReviewConfirmation) => ({
        ...prevState,
        isConfirmBtnLoading: false,
        isCancelBtnDisable: false,
        isSaveBtnLoading:false,
      }));
    } finally {
      setReviewConfirmation((prevState: ReviewConfirmation) => ({
        ...prevState,
        isConfirmBtnLoading: false,
        isCancelBtnDisable: false,
        isSaveBtnLoading:false,
      }));
    }
  };

  const validateField = async (name: string, value: string | Date | any) => {
    try {
      if ((["employee_identification_number"].includes(name) && value?.length === 11 && regexEmployeeIdentificationNumber.test(licenseReview?.employee_identification_number as string))) {
        setLicenseReview((prevState: any) => ({
          ...prevState,
          errors: { ...(prevState.errors || {}), [name]: '' }
        }));
      } else {
        await sotTaxStampsValidation.validateAt(name, { [name]: value });
        setLicenseReview((prevState: any) => ({
          ...prevState,
          errors: { ...(prevState.errors || {}), [name]: '' }
        }));
      }
    } catch (error: any) {
      if (error.errors && error.errors.length > 0) {
        setLicenseReview((prevState: any) => ({
          ...prevState,
          errors: { ...(prevState.errors || {}), [name]: error.errors[0] }
        }));
      }
    }
  };

  const validateFields = async () => {
    try {
      await sotTaxStampsValidation.validate(licenseReview, { abortEarly: false });
    } catch (error: any) {
      if (error.errors && error.errors.length > 0) {
        const errors = error?.inner?.reduce((acc: { [key: string]: string }, err: any) => {
          acc[err.path] = err.message;
          return acc;
        }, {});
        setLicenseReview((prevState: any) => ({
          ...prevState,
          errors
        }));
        throw new Error(error?.errors[0]);
      }
    }
  };

  const formatEmployeeIdentificationNumber = (value: string) => {
    if (value) {
      value = value.replace(/[a-zA-Z]/g, '');
      if (value.length > 10) return licenseReview?.employee_identification_number || "";
      else if (value.includes("-")) return value;
      else return value.length > 2 ? `${value?.slice(0, 2)}-${value?.slice(2)}` : value;
    }
    return "";
  };

  const formatControlNumber = (value: string) => {
    if (!value) return ''
    if (value.length !== 13) return value|| ""
    else if (value.length === 13) {
      value = value.substring(0, 7) + "-" + value.substring(7, 10) + "-" + value.substring(10)
    }
    return value;
  };

  const onChangeInputs = async (value: string | boolean | any, fieldName: string) => {
    await validateField(fieldName, value as string);
    if (fieldName === "employee_identification_number")
      value = formatEmployeeIdentificationNumber(value as string);
    else if (fieldName === "number_of_locations" && value?.length > 9)
      value = licenseReview?.number_of_locations;

    if (fieldName === "type_of_operation") {
      setLicenseReview((prevState: any) => ({
        ...prevState,
        [fieldName]: value,
        annual_tax_rate: operationTypes[value as string]?.rate,
        errors: {...prevState.errors, [fieldName]: '', annual_tax_rate:''}
      }));  
      return;
    }
    setLicenseReview((prevState: any) => ({
      ...prevState,
      [fieldName]: value
    }));
  };

  const licenseSchema = yup.object({
    license_key: yup.string().trim().required("License number is required.").matches(/^[0-9]{8}$/, "Invalid license number"),
  })
  const {
    register,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(licenseSchema),
    mode: 'onChange',
    values: {
      license_key: ''
    }
  });

  const licenseSearchHandler = () => {
    setIsLoading(true);
    dispatch(setQuery([licenseReview?.license_key as string]))
  }

  // const key:any =  license?.licenses?.filter((item:any) => item.isPrimaryForInbound)[0].licenseKey
  function renderLicenseId() {
    return (
      <form onSubmit={(e) => {
        e.preventDefault();
        if (licenseReview?.license_key) {
          licenseSearchHandler();
          onOpenSearchNewDealerModal()
        }
      }}>
        <div className="transfer-form__search-result" style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          <Input
            style={{ paddingTop: '4px', minWidth: '250px' }}
            color={'black'}
            value={licenseReview?.license_key || ''}
            placeholder='Search a FFL number, Zip code, Control number, Business Name, City, Email, Phone number here.....'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setLicenseReview((prevState: any) => ({ ...prevState, 'license_key': e.target.value }));
            }
            }
            name="license_key"
          >
          </Input>
          <Button
            onClick={() => {
              if (licenseReview?.license_key) {
                licenseSearchHandler();
                onOpenSearchNewDealerModal()
              }
            }}
            className="transfer-form__search-btn store-license-btn"
            isDisabled={!licenseReview?.license_key}
          >
            {isLoading && !errors?.license_key ? (
              <Spinner size="sm" />
            ) : (
              <img src="/images/ui/search-icon-blue.svg" />

            )}
          </Button>
        </div>
        {licenseReview?.errors && licenseReview?.errors?.license_key ?
                                <Box sx={{width:'70%'}}>
                                  <div className="edit-error">
                                    {licenseReview.errors && licenseReview?.errors?.license_key && (
                                      <>* Please enter valid license number.</>
                                    )}
                                  </div>
                                </Box> : ""
                              }
        </form>
    );
  }

  const downloadFile2 = async (path: string) => {
    setDownloadingFile2(true);
    await s3Utils.getFile(path, "licenseReader", { public: '' });
    setDownloadingFile2(false);
  };

  const handleDownload = async () => {
    const url = fullURL(licenseReview?.license_path || "");
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = licenseReview?.license_path?.split("/").pop() || "";
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading the image:', error);
    }
  };
  const openFFLDealerDetails = () => {
    if (licenseReview?.dealer_id) {
      const linkUrl = `/dealers-search/${licenseReview?.dealer_id}`;
      window.open(linkUrl, '_blank');
    }
  }

  const needToShowErrorInLicenseKeyAttribute = useMemo(() => {
    if (errors.license_key) return errors.license_key.message
    else if (notFoundError) return notFoundError
    else return null
  }, [errors.license_key, notFoundError])

  const handleChange = async (event: any) => {
    setReviewConfirmation((prevState: ReviewConfirmation) => ({
      ...prevState,
      rejectedReason: event.target.value
    }));
  };

  const handleTextChange = async (event: any) => {
    const value = event.target.value;
    try {
      await textareaLengthValidation.validateAt('textarea', { 'textarea': value });
      setReviewConfirmation((prevState: ReviewConfirmation) => ({
        ...prevState,
        note: event.target.value,
        error: ''
      }));
    } catch (error: any) {
      if (error.errors && error.errors.length > 0) {
        setReviewConfirmation((prevState: ReviewConfirmation) => ({
          ...prevState,
          error: error.errors[0]
        }));
      }
    }
  };

  const closeReviewConfirmationModal = () => {
    onCloseReviewConfirmationModal();
    setReviewConfirmation({
      header: "",
      isCancelBtnDisable: false,
      isConfirmBtnDisable: false,
      isConfirmBtnLoading: false,
      isSaveBtnLoading: false,
      rejectedReason: "",
      note: "",
      error: "",
      status: "",
    });
  }

  const disableConfirmButton = () => {
    if (reviewConfirmation.error || (reviewConfirmation.status === "rejected" && !reviewConfirmation.rejectedReason)) {
      return true;
    }
    return false;
  }

  const downloadIcon2 = () => {
    return (
      <>
        {downloadingFile2 ? (
          <button className="license_details__download-btn download-license-height">
            <Spinner size="sm" />
          </button>
        ) : (
          <Box
            display={'inline'}
            position={'relative'}
            top={'6px'}
            pl={2}
          >
          <Tooltip
            hasArrow
            placement="top"
            label="Download Original SOT"
            color={'black'}
            bg={'white'}
          >
            <button
              className="license_details__download-btn download-license-height"
              onClick={() => downloadFile2(licenseReview?.object_path as string)}
              disabled={!licenseReview?.object_path}
            >
              <Image src="/images/ui/download.svg" />
            </button>
          </Tooltip>
          </Box>
        )}
      </>
    );
  };

  const downloadIcon = () => {
    return <Box
      display={'inline-block'}
      position={'relative'}
      top={'6px'}
      pl={2}
      cursor={'pointer'}
      onClick={handleDownload}
    >
      <Tooltip
        hasArrow
        placement="top"
        label="Download certificate"
        color={'black'}
        bg={'white'}
      >
        <Image src="/images/ui/download.svg" mr={2} />
      </Tooltip>
    </Box>
  }
  const callLicenseDetails = async (license_key: any) => {
    onOpen()
    const response = await licenseDetailsNewApi(license_key)
    setLicense(response)
    setLicenseKey(license_key)
    setLicenseReview((prevState: any) => ({
      ...prevState,license_key: license_key, business_name: license?.primaryBusinessName,
      errors: { ...(prevState.errors || {}), business_name: '', license_key: '' }
    }))
    
    setIsLoading(false)
  }
  
  const ModalLoader = () => {
    return (<div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.3)", zIndex: "10000", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Spinner />
    </div>)
  }

  useEffect(() => {
    if (license?.primaryBusinessName)
      setLicenseReview((prev: any) => ({
        ...prev,
        business_name: license?.primaryBusinessName,
        dealer_id: license?.dealerId,
      }));
  }, [license]);

  const onSave = async () => {
    await validateFields()
    await updateCertificateStatus(licenseReview?.status ? licenseReview?.status.toLowerCase() : "pending", true)
  }
  useEffect(() => {
    if (isOpen) {
      dispatch(setDocumentsRequiredDetails({}));
    }
  }, [isOpen]); 

  const [dealer_id, setDealerId] = useState<string>('');
  useEffect(() => {
    if(licenseReview?.dealer_id){
      setDealerId(licenseReview?.dealer_id || ""); 
    }
  }, [licenseReview?.dealer_id]);

  useEffect(() => {
    if (licenseReview?.license_key && (licenseReview?.license_key.length === 8 || licenseReview?.license_key.length === 20 ) && isOpen == true) {
      dispatch(callToGetLicenseRecordDetails({ id: licenseReview?.license_key }));
    } else {
      dispatch(setDocumentsRequiredDetails({}));
      setDealerId('');
    }
  }, [licenseReview?.license_key, isOpen]);

  const validLicenseRecordDetails = getValidLicenseRecord()

  useEffect(() => {
    if (validLicenseRecordDetails?.dealerId && validLicenseRecordDetails?.fullLicenseKeys?.[0]) {
      dispatch(callToGetDocumentStatus({ id: validLicenseRecordDetails?.fullLicenseKeys?.[0] }));
      setDealerId(validLicenseRecordDetails?.dealerId);
    }
    else{
      setDealerId('');
    } 
  }, [validLicenseRecordDetails]);
  const isDealerDetailsLoading = getIsDealerDetailsLoading();
  const documentsRequiredDetails = getDocumentsRequiredDetails()
  return (
    <>
      {renderData()}
      <Box className="approval-header-wrapper">
        <Heading size={'md'}>SOT Tax Stamps</Heading>
        <Box className="a-view-toggle-wrapper">
          <Text className="view-toggle-text">View :</Text>
          <Flex
            className="license-view-toggle"
            role="group"
            aria-label="SOT list layout"
          >
            <Box
              as="button"
              type="button"
              className={`license-view-toggle__segment${
                sotListView === 'table'
                  ? ' license-view-toggle__segment--active'
                  : ''
              }`}
              onClick={() => setSotListView('table')}
              aria-pressed={sotListView === 'table'}
            >
              <SOTTableViewIcon
                color={sotListView === 'table' ? '#0C1E46' : '#4A5565'}
              />
              <span className="license-view-toggle__label">Table</span>
            </Box>
            <Box
              as="button"
              type="button"
              className={`license-view-toggle__segment${
                sotListView === 'gallery'
                  ? ' license-view-toggle__segment--active'
                  : ''
              }`}
              onClick={() => setSotListView('gallery')}
              aria-pressed={sotListView === 'gallery'}
            >
              <SOTGalleryViewIcon
                color={sotListView === 'gallery' ? '#0C1E46' : '#4A5565'}
              />
              <span className="license-view-toggle__label">Gallery</span>
            </Box>
          </Flex>
        </Box>
      </Box>
      {sotListView === 'table' ? (
        <div style={gridStyle} className={className}>
          <AgGridReact
            className={'ag-theme-alpine'}
            ref={gridRef}
            gridOptions={gridOptions}
          />
        </div>
      ) : (
        <SOTDocumentsGridView
          onDocumentClick={openSOTDetailPage}
          onHoverReject={openRejectFromGalleryRow}
          onHoverRotate={performRotateForSOTDoc}
          getHoverReviewKind={getSOTGalleryHoverReviewKind}
          onHoverReview={markSOTReviewedFromGallery}
          galleryThumbnailUrlByDocId={galleryImagePathOverrideByDocId}
          galleryThumbnailReloadNonceByDocId={galleryThumbnailReloadNonceByDocId}
          refreshTrigger={documentsGalleryRefreshKey}
        />
      )}
      <ModalComponent onClose={() => {
        setLicenseReview((prevState: any) => ({
          ...prevState,
          license_key: null,
          business_name: null,
          position_title: null,
          errors: {}
        }))
        setOcrExpDate(null);
        setSignatureDate(null);
        setLicense({})
        setLicenseKey('')
        setNotFoundError('')
        setIsEditingEnabled(false)
        reset();
        onClose();
      }}
        isOpen={isOpen}
        title={<>SOT Approval</>}
        variant={2}
        className="sot-tax-stamps-modal"
        padding={'25px !important'}
        footer={
          <>
          <Button 
              background= "#0C1E46"
               colorScheme= "blue"
              padding="8px 20px"
              marginRight="20px"
              isLoading={reviewConfirmation.isSaveBtnLoading}
              sx={{outline: 'none !important'}}
              isDisabled={(licenseReview &&
                licenseReview?.errors &&
                Object.values(licenseReview?.errors).some((val) => val !== '') ? true : false)}
              onClick={() => { onSave()}}
            >
              Save
            </Button>
            <Button
              {...(checkStatus(licenseReview?.status, "approved") && { leftIcon: <Image src="/images/ui/success.svg" />, isDisabled: true })}
              {...(!checkStatus(licenseReview?.status, "pending") ? { colorScheme: "green", variant: 'outline' } : { background: "#0C1E46", colorScheme: "blue" })}
              padding="8px 20px"
              marginRight="20px"
              sx={{outline: 'none !important'}}
              onClick={() => { onOpenCertificateStatus("approved") }}
            >
              {!checkStatus(licenseReview?.status, "approved") ? "Approve" : "Approved"}
            </Button>
            <Button
              {...(checkStatus(licenseReview?.status, "rejected") && { leftIcon: <Image src="/images/ui/reject.svg" />, isDisabled: true })}
              {...(!checkStatus(licenseReview?.status, "pending") ? { colorScheme: "red", variant: 'outline', padding: "8px 20px" } : { background: "#ffffff", color: "#0C1E46", colorScheme: "blue", border: "1px solid #0C1E46", padding: "8px 11px" })}
              onClick={() => { onOpenCertificateStatus("rejected") }}
              marginRight="20px"
              sx={{outline: 'none !important'}}
            >
              {!checkStatus(licenseReview?.status, "rejected") ? "Reject" : "Rejected"}
            </Button>
            <Button
              colorScheme="blue"
              background="#ffffff"
              color="#0C1E46"
              border="1px solid #0C1E46"
              padding="8px 11px"
              sx={{outline: 'none !important'}}
              onClick={() => {
                setLicense({})
                setLicenseKey('')
                setNotFoundError('')
                setIsEditingEnabled(false)
                reset()
                onClose()
              }}
            >
              Cancel
            </Button>
          </>
        }
      >
        <div className='approval-model-body'>
          <Grid templateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }} gap={6}>
            <GridItem>
              <TableContainer className='sot-table-container' flex={1}>
                <Table>
                  <Tbody>
                    <Grid
                      templateRows='repeat(1, 1fr)'
                      templateColumns={{
                        base: "repeat(1, 1fr)",
                        md: "repeat(2, 1fr)",
                      }}
                      sx={{ borderBottom: "2px solid gray" }}
                      columnGap={6}
                    >
                      <Grid
                        templateRows='50px'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={ 
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            License Number:
                            {(dealer_id) && (
                              <img 
                                src="/images/ui/blue-link.svg" 
                                alt="License" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  window.open(`/dealers-search/${dealer_id}`, '_blank');                                }} 
                                style={{ 
                                  width: '16px', 
                                  height: '16px', 
                                  cursor: 'pointer'
                                }} 
                              />
                            )}
                          </div>} px={"0"} />
                        </GridItem>
                        <GridItem style={{ width: '100%' }}>
                          <Tr verticalAlign={"start"}>
                            <Td textAlign={'left'} fontWeight={'bold'} p={"0"} style={{ minWidth: '300px', width: '100%', display: 'flex' }}>
                              <FormControl>

                                {renderLicenseId()}
                              </FormControl>
                            </Td>
                          </Tr>
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Name:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <Tr verticalAlign={"start"}>
                            <Td textAlign={'left'} fontWeight={'bold'} p={"0"}>
                              {license?.primaryBusinessName || licenseReview?.business_name || '-'}
                              {licenseReview?.errors && licenseReview?.errors?.business_name ?
                                <div className="">
                                  <div className="edit-error">
                                    {licenseReview.errors && licenseReview?.errors.business_name && (
                                      <>* {licenseReview?.errors.business_name}</>
                                    )}
                                  </div>
                                </div> : ""
                              }
                            </Td>
                          </Tr>
                        </GridItem>
                      </Grid>
                    </Grid>
                    <Grid
                      templateRows='repeat(1, 1fr)'
                      templateColumns={{
                        base: "repeat(1, 1fr)",
                        md: "repeat(2, 1fr)",
                      }}
                      columnGap={6}
                    >
                      <Grid
                        templateRows='50px'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Principal Business Name:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <ControlledInput
                            className="sot-tax-stamp__input contact-detail-form__contact-name"
                            type="text"
                            placeholder="Principal Business Name"
                            value={licenseReview?.principal_business_name || ""}
                            onChange={(event) =>
                              onChangeInputs(
                                event.target.value,
                                'principal_business_name'
                              )
                            }
                          />
                          {licenseReview?.errors && licenseReview?.errors?.principal_business_name ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.principal_business_name && (
                                  <>* {licenseReview?.errors.principal_business_name}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Annual Tax Rate:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <Tr verticalAlign={"start"}>
                            <Td textAlign={'left'} fontWeight={'bold'} p={"0"}>
                              {!licenseReview?.type_of_operation ? "____" : operationTypes[licenseReview?.type_of_operation as string]?.rate}
                            </Td>
                          </Tr>
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px auto'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Principal Business Address:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <Textarea
                            onChange={(event) => {
                              onChangeInputs(
                                event.target.value,
                                'name_and_principal_business_address'
                              );
                              setTextareaEvent(event.target.selectionStart);
                            }
                            }
                            value={licenseReview?.name_and_principal_business_address || ""}
                            name="name_and_principal_business_address"
                            className="sot-transfer-form__textarea"
                            ref={principalBusinessAddress as any}
                          />
                          {licenseReview?.errors && licenseReview?.errors?.name_and_principal_business_address ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.name_and_principal_business_address && (
                                  <>* {licenseReview?.errors.name_and_principal_business_address}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px auto'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Types of Operation Conducted:'} px={"0"} />
                        </GridItem>
                        <GridItem sx={{ width: "100%" }}>
                          <Select
                            placeholder='Select option'
                            sx={{ borderColor: "black" }}
                            onChange={(event) =>
                              onChangeInputs(
                                event.target.value,
                                'type_of_operation'
                              )
                            }
                          >
                            {
                              Object.entries(operationTypes).map(([key, val]) => <option value={key} selected={key === licenseReview?.type_of_operation}>({key}) {val.name}</option>)
                            }
                          </Select>
                          {licenseReview?.errors && licenseReview?.errors?.type_of_operation ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.type_of_operation && (
                                  <>* {licenseReview?.errors.type_of_operation}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Actual Physical Business Name:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <ControlledInput
                            className="sot-tax-stamp__input contact-detail-form__contact-name"
                            type="text"
                            placeholder="Actual Physical Business Name"
                            value={licenseReview?.actual_physical_business_name || ""}
                            onChange={(event) =>
                              onChangeInputs(
                                event.target.value,
                                'actual_physical_business_name'
                              )
                            }
                          />
                          {licenseReview?.errors && licenseReview?.errors?.actual_physical_business_name ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.actual_physical_business_name && (
                                  <>* {licenseReview?.errors.actual_physical_business_name}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Number of Locations:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <ControlledInput
                            className="sot-tax-stamp__input contact-detail-form__contact-name"
                            type="number"
                            placeholder="Number of Locations"
                            value={licenseReview?.number_of_locations || ""}
                            onChange={(event) =>
                              onChangeInputs(
                                event.target.value,
                                'number_of_locations'
                              )
                            }
                          />
                          {licenseReview?.errors && licenseReview?.errors?.number_of_locations ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.number_of_locations && (
                                  <>* {licenseReview?.errors.number_of_locations}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px auto'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Actual Physical Business Address:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <Textarea
                            onChange={(event) => {
                              onChangeInputs(
                                event.target.value,
                                'actual_physical_business_address'
                              );
                              setTextareaEvent(event.target.selectionStart);
                            }
                            }
                            value={licenseReview?.actual_physical_business_address || ""}
                            name="actual_physical_business_address"
                            className="sot-transfer-form__textarea"
                            ref={physicalBusinessAddress as any}
                          />
                          {licenseReview?.errors && licenseReview?.errors?.actual_physical_business_address ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.actual_physical_business_address && (
                                  <>* {licenseReview?.errors.actual_physical_business_address}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows={{
                          base: '50px auto',
                        }}
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Dates of Special Tax Period:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <Grid
                            templateRows={{
                              base: 'repeat(2, 1fr)',
                              lg: 'repeat(1, 1fr)',
                            }}
                            templateColumns={{
                              base: 'repeat(1, 1fr)',
                              lg: 'repeat(2, 1fr)',
                            }}
                            gap={3}
                          >
                            <GridItem>
                              <FlatDatePickr
                                  label=""
                                  value={fixTimezoneOffset(licenseReview?.special_tax_period_start_date) || null}
                                  onDateChange={(date: Date) => onDateChange(date, 'special_tax_period_start_date')}
                                  options={{ placeholderText: '-Start Date-' }}
                                  classNameValue="transfer-form__input order-date-picker"
                              />
                              {licenseReview?.errors && licenseReview?.errors?.special_tax_period_start_date ?
                                <div className="">
                                  <div className="edit-error">
                                    {licenseReview.errors && licenseReview?.errors.special_tax_period_start_date && (
                                      <>* {licenseReview?.errors.special_tax_period_start_date}</>
                                    )}
                                  </div>
                                </div> : ""
                              }
                            </GridItem>
                            <GridItem>
                              <FlatDatePickr
                                  label=""
                                  value={fixTimezoneOffset(licenseReview?.special_tax_period_end_date) || null}
                                  onDateChange={(date: Date) => onDateChange(date, 'special_tax_period_end_date')}
                                  options={{ placeholderText: '-End Date-' }}
                                  classNameValue="transfer-form__input order-date-picker"
                              />
                              {licenseReview?.errors && licenseReview?.errors?.special_tax_period_end_date ?
                                <div className="">
                                  <div className="edit-error">
                                    {licenseReview.errors && licenseReview?.errors.special_tax_period_end_date && (
                                      <>* {licenseReview?.errors.special_tax_period_end_date}</>
                                    )}
                                  </div>
                                </div> : ""
                              }
                            </GridItem>
                          </Grid>
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Receipt Date:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <FlatDatePickr
                              label=""
                              value={fixTimezoneOffset(licenseReview?.date_of_tax_stamp_receipt) || null}
                              onDateChange={(date: Date) => onDateChange(date, 'date_of_tax_stamp_receipt')}
                              classNameValue="transfer-form__input receipt-date-picker"
                          />
                          {licenseReview?.errors && licenseReview?.errors?.date_of_tax_stamp_receipt ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.date_of_tax_stamp_receipt && (
                                  <>* {licenseReview?.errors.date_of_tax_stamp_receipt}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Control Number:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                         <ControlledInput
                            className="sot-customize-input"
                            type="text"
                            placeholder="Control Number"
                            value={licenseReview?.control_number || ""}
                            onChange={(event: any) =>
                              onChangeInputs(
                                event.target.value?.trim(),
                                'control_number'
                              )
                            }
                            />
                          {licenseReview?.errors && licenseReview?.errors?.control_number ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.control_number && (
                                  <>* {licenseReview?.errors.control_number}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows={{
                          base: '50px auto',
                        }}
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Employer Identification Number:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <InputMask
                            id="formattedInput"
                            mask="99-9999999"
                            placeholder="XX-XXXXXXX"
                            value={licenseReview?.employee_identification_number || ""}
                            onChange={(event: any) =>
                              onChangeInputs(
                                event.target.value,
                                'employee_identification_number'
                              )
                            }
                          >
                            {(inputProps: any) => <input
                              {...inputProps}
                              type="text"
                              className='sot-customize-input'
                            />
                            }
                          </InputMask>
                          {licenseReview?.errors && licenseReview?.errors?.employee_identification_number ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.employee_identification_number && (
                                  <>* {licenseReview?.errors.employee_identification_number}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                      <Grid
                        templateRows='50px'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem>
                          <TableElement noBorder={true} label={'Bio Metric Scanner:'} px={"0"} />
                        </GridItem>
                        <GridItem>
                          <Switch
                            size='md'
                            onChange={(event) =>
                              onChangeInputs(
                                event.target.checked,
                                'bio_metric_scanner'
                              )
                            }
                            isChecked={!!licenseReview?.bio_metric_scanner}
                          />
                          {licenseReview?.errors && licenseReview?.errors?.bio_metric_scanner ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.bio_metric_scanner && (
                                  <>* {licenseReview?.errors.bio_metric_scanner}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </GridItem>
                      </Grid>
                       {licenseReview?.action_performed_by && (
                        <Grid
                            templateRows='50px'
                            templateColumns='repeat(1, 1fr)'
                        >
                            <GridItem>
                                <TableElement noBorder={true} label={'Manual Review Requested By:'} px={"0"} />
                            </GridItem>
                            <GridItem>
                            <Box>{licenseReview?.action_performed_by
                                 ? getFormattedTextForManualReview(licenseReview?.action_performed_by, licenseReview?.manual_review_requested_by) : ''}</Box>
                            </GridItem>
                        </Grid>
                        )}
                        {licenseReview?.manual_review_requested_at && (
                        <Grid
                            templateRows='50px'
                            templateColumns='repeat(1, 1fr)'
                        >
                            <GridItem>
                                <TableElement noBorder={true} label={'Manual Review Requested At:'} px={"0"} />
                            </GridItem>
                            <GridItem>
                            <Box>{licenseReview?.manual_review_requested_at
                        ? getFormattedDateTime(licenseReview?.manual_review_requested_at  ) : ''}</Box>
                            </GridItem>
                        </Grid>
                        )}
                        <Grid
                            templateRows='50px'
                            templateColumns='repeat(1, 1fr)'
                        >
                            <GridItem>
                                <TableElement noBorder={true} label={'Rejection Reason:'} px={"0"} />
                            </GridItem>
                            <GridItem>
                                 <Select
                                      placeholder='Select reason*' 
                                      my={3}
                                      onChange={(event) =>{
                                         setReviewConfirmation({
                                           ...reviewConfirmation,
                                           rejectedReason: event.target.value,
                                         })
                                        setReviewInputVal({...reviewInputVal, rejectReason: event.target.value})
                                      }}
                                      disabled={!checkStatus(licenseReview?.status, "rejected")}
                                      required
                                   >{
                                       Object.entries(reviewRejectedReasonsSot).map(([key, val]) => <option value={val as string} selected={val === reviewInputVal?.rejectReason}>{val as string}</option>)
                                    }
                                 </Select>
                            </GridItem>
                        </Grid>
                        {!checkStatus(licenseReview?.status, "pending") && <Grid
                            templateRows='50px auto'
                            templateColumns='repeat(1, 1fr)'
                        >
                            <GridItem>
                                <TableElement noBorder={true} label={'Note:'} px={"0"} />
                            </GridItem>
                            <GridItem>
                                <Textarea
                                  // isDisabled={true}
                                  name="rejected_reason"
                                  className="sot-transfer-form__textarea"
                                  value={reviewInputVal?.note || ""}
                                  onChange={(e) => {
                                      setReviewConfirmation({
                                        ...reviewConfirmation,
                                        note: e.target.value,
                                      })
                                      setReviewInputVal({
                                        ...reviewInputVal,
                                        note: e.target.value,
                                      })
                                  }}
                                />
                            </GridItem>
                        </Grid>}
                    </Grid>
                  </Tbody>
                </Table>
              </TableContainer>
            </GridItem>
            <GridItem>
              <TableContainer flex={1}>
                <Table>
                  <Tbody>
                    <Grid
                      templateRows='repeat(1, 1fr)'
                      pb={10}
                    >
                      {licenseReview?.object_path ? <Grid
                        templateRows='60px'
                      >
                        <GridItem>
                          {/* <TableElement noBorder={true} label={'SOT Preview:'} px={"0"} renderFunction={downloadIcon} /> */}
                          <TableContainer>
                            <Table>
                              <Tbody display='flex' justifyContent='space-between' alignItems='center'>
                                <Box className="transfer_flex_row" sx={{ gap: '8px', marginTop: '8px' }}>
                                  <Box className={`doc_box ${documentsRequiredDetails?.hasValidCertificate ? 'doc_active' : ''}`}>FFL License</Box>
                                  <Box className={`doc_box ${documentsRequiredDetails?.hasValidSOT ? 'doc_active' : ''}`}>SOT</Box>
                                  <Box className={`doc_box ${documentsRequiredDetails?.hasValidLOA ? 'doc_active' : ''}`}>LOA</Box>
                                </Box>
                                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                  <TableElement
                                    label={'SOT Preview:'}
                                    className="license-file-path"
                                    renderFunction={downloadIcon}
                                  ></TableElement>
                                  <TableElement
                                    label={'Original File'}
                                    className="license-file-path"
                                    renderFunction={downloadIcon2}
                                  ></TableElement>
                                </div>
                              </Tbody>
                            </Table>
                          </TableContainer>
                        </GridItem>
                      </Grid> : <></>}
                      <Grid
                        templateRows='auto'
                        templateColumns='repeat(1, 1fr)'
                      >
                        <GridItem
                          w={"100%"}
                          h={"100%"}
                        >
                          {
                            licenseReview?.license_path && !(licenseReview?.license_path.split('.').pop() === 'pdf') ?
                              <MapInteractionCSS
                                showControls
                                defaultValue={{
                                  scale: 1,
                                  translation: { x: 0, y: 20 }
                                }}
                                minScale={0.5}
                                maxScale={3}
                                translationBounds={{
                                  xMax: 400,
                                  yMax: 200
                                }}
                                btnClass={'map-interection-buttons'}
                              >
                                <Image src={licenseReview?.license_path ? fullURL(licenseReview?.license_path) : ""} alt='sot-image' className={`sot-image`} />
                              </MapInteractionCSS>
                              :
                              <iframe src={licenseReview?.license_path ? fullURL(licenseReview?.license_path) : ""} name={"SOT License Preview"} title={"SOT License Preview"} id='sot-license-iframe' className='sot-license-preview'></iframe>
                          }
                        </GridItem>
                      </Grid>
                    </Grid>
                  </Tbody>
                </Table>
              </TableContainer>
            </GridItem>
          </Grid>
        </div>
      </ModalComponent>
      <ReviewConfirmationModal
        isOpen={isReviewConfirmationMadalOpen}
        onClose={() => {
          setReviewConfirmation({
            ...reviewConfirmation,
            rejectedReason: '',
            note: '',
            error: '',
          })
          closeReviewConfirmationModal()
        }}
        modalHeader={reviewConfirmation.header}
        isCancelBtnDisable={reviewConfirmation.isCancelBtnDisable}
        isConfirmBtnDisable={disableConfirmButton()}
        isConfirmBtnLoading={reviewConfirmation.isConfirmBtnLoading}
        onClickConfirmBtn={(isEmailIgnored) => { updateCertificateStatus(reviewConfirmation.status, false, isEmailIgnored) }}
        fields={reviewRejectedReasonsSot}
        selectedValue={reviewConfirmation.rejectedReason}
        handleChange={handleChange}
        handleTextChange={handleTextChange}
        hideDropdown={reviewConfirmation.status !== "approved"}
        error={reviewConfirmation.error}
        approvalDetails={licenseReview}
      />
      <PreviewLicense
        isOpen={isLicensePreviewOpen}
        onClose={onCloseLicensePreview}
        onOpen={onLicensePreviewOpen}
        blob={licenseBlob}
        title={'License Preview'}
        filePath={licenseReview?.license_path as string}
      />
      <ModalComponent
        footer={null}
        isOpen={isSearchNewDealerModalOpen}
        onClose={() => {
          onCloseSearchNewDealerModal()
          setIsLoading(false)
        }}
        children={
          <>
            <ExploreV2
              cognitoGroup={false}
              isSuperAdmin={false}
              isStoreAdmin={true}
              isAssignDealerModal={true}
              assignNewDealerInitialZipCode={licenseReview?.license_key as string}
              onCloseAssignDealerModal={() => {
                onCloseSearchNewDealerModal();
              }}
              type={"admin-approval"}
              setLicenseKey={callLicenseDetails}
            />
            {isAssigningTransferDealer && <ModalLoader />}
          </>
        }
        title={"Select New License"}
        variant={2}
        maxWidth='1360px'
        className="assign-new-dealer-modal"
      />
    </>
  );
}

export default SOTTaxStampsTable;
